import { Link } from 'react-router-dom';
import { serviceCategories } from '@/data/services';
import { usePopup } from '@/contexts/PopupContext';
import { ArrowRight, Mail, Phone, MapPin, Clock } from 'lucide-react';

export default function MetaFooter() {
  const { openPopup } = usePopup();

  const quickLinks = [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Contact Us', to: '/contact' },
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms of Service', to: '/terms-of-service' },
  ];

  const footerCategories = serviceCategories.slice(0, 3);

  return (
    <footer className="bg-[#022C22] text-white" data-testid="meta-footer">

      {/* Top Section — Brand + Contact */}
      <div className="border-b border-emerald-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Brand */}
            <div>
              <Link to="/" className="inline-block" data-testid="meta-footer-logo">
                <img src="/assets/img/logo-white.png" alt="Trademark Pact" className="h-12 w-auto" />
              </Link>
              <p className="text-white/70 text-sm leading-relaxed mt-4 max-w-md">
                Professional trademark registration, intellectual property protection, and corporate services trusted by businesses across the United States.
              </p>
              <button
                onClick={() => openPopup()}
                className="mt-6 bg-[#10B981] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-emerald-400 transition-colors inline-flex items-center gap-2"
                data-testid="meta-footer-cta"
              >
                Get Started <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="meta-footer-contact">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-900/60 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-1">Email</h4>
                  <p className="text-sm text-white/80">info@trademarkpact.com</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-900/60 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-1">Phone</h4>
                  <p className="text-sm text-white/80">+1 (877) 685-1682</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-900/60 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-1">Location</h4>
                  <p className="text-sm text-white/80">18952 MacArthur Blvd Suite 100, Irvine, CA 92612</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-900/60 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-1">Hours</h4>
                  <p className="text-sm text-white/80">Mon-Fri 9AM - 6PM EST</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Section — Links Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-12">

          {/* Service Category Columns */}
          {footerCategories.map((cat) => (
            <div key={cat.id}>
              <h4 className="font-heading font-semibold text-sm text-white mb-4">
                {cat.name}
              </h4>
              <ul className="space-y-2.5">
                {cat.services.slice(0, 5).map((svc) => (
                  <li key={svc.slug}>
                    <Link
                      to={`/services/${svc.slug}`}
                      className="text-white text-sm hover:text-white transition-colors"
                      data-testid={`meta-footer-svc-${svc.slug}`}
                    >
                      {svc.title}
                    </Link>
                  </li>
                ))}
                {cat.services.length > 5 && (
                  <li>
                    <Link
                      to={`/services/${cat.services[0].slug}`}
                      className="text-white text-xs font-medium hover:text-white transition-colors inline-flex items-center gap-1"
                    >
                      View all {cat.services.length} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          ))}

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-white mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-white text-sm hover:text-white transition-colors"
                    data-testid={`meta-footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-emerald-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/50 text-sm">
              &copy;  {new Date().getFullYear()} Trademark Pact. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-white hover:text-white transition-colors" data-testid="meta-footer-privacy">
                Privacy Policy
              </Link>
              <span className="text-white">|</span>
              <Link to="/terms-of-service" className="text-white hover:text-white transition-colors" data-testid="meta-footer-terms">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
